import { Controller } from "@hotwired/stimulus"

// Handles contact details form validation and submission
export default class extends Controller {
  static targets = ["firstName", "lastName", "email", "phone"];

  static values = {
    customer: Boolean
  };

  connect() {
    this.validations = {
      firstName: this.validatePresence,
      lastName: this.validatePresence,
      email: this.validateEmail,
      phone: this.validatePhone
    };

    this.formValid = false;
    this.isSubmitting = false;
    this.lastSubmittedData = this.collectContactDetails();
    this.submitTimeout = null;
    this.checkFormValidity();
  }

  disconnect() {
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
      this.submitTimeout = null;
    }
  }

  // Validation triggers on field blur
  validateField(event) {
    const field = event.target;
    let fieldName = field.name?.split('[')?.pop()?.replace(']', '') || field.name;

    // Map form field names to validation keys
    const nameMap = {
      guest_first_name: 'firstName',
      guest_last_name: 'lastName',
      guest_email: 'email',
      guest_phone: 'phone'
    };
    fieldName = nameMap[fieldName] || fieldName;

    const validator = this.validations[fieldName];

    if (validator) {
      const error = validator.call(this, field.value, fieldName);
      this.displayFieldError(field, error);
    }

    this.checkFormValidity();
    this.scheduleAutoSubmit();
  }

  // Auto-submit contact details when form becomes valid
  async autoSubmitContactDetails(payload = null) {
    if (!this.formValid) {
      return;
    }

    const contactData = payload || this.collectContactDetails();
    if (!this.hasContactChanges(contactData)) {
      return;
    }

    // Prevent duplicate submissions
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    try {
      const result = await this.updateContactDetails(contactData);
      if (result.success) {
        if (result.contact) {
          this.applyServerContact(result.contact);
        }

        this.lastSubmittedData = this.collectContactDetails();
        this.showSuccess(result.message || "Contact details saved.");
      } else {
        const errorMessage = result.error || "Failed to update contact details. Please try again.";
        this.displayFormError(errorMessage, { toast: true });
      }
    } catch (error) {
      console.error("Contact details update error:", error);
      this.displayFormError("We couldn't save your contact details. Please try again.", { toast: true });
    } finally {
      this.isSubmitting = false;
    }
  }

  scheduleAutoSubmit() {
    if (!this.formValid) {
      if (this.submitTimeout) {
        clearTimeout(this.submitTimeout);
        this.submitTimeout = null;
      }
      return;
    }

    const contactData = this.collectContactDetails();
    if (!this.hasContactChanges(contactData)) {
      return;
    }

    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
    }

    this.submitTimeout = setTimeout(() => {
      this.submitTimeout = null;
      this.autoSubmitContactDetails(contactData).then();
    }, 250);
  }

  // Submit only when all fields are valid (for manual button clicks)
  async submitContactDetails(event) {
    if (!this.formValid) {
      event.preventDefault();
      this.displayFormError("Please complete all required fields correctly.", { toast: true });
      return false;
    }
  }

  // Individual field validators
  validatePresence(value, fieldName) {
    if (!value || value.trim().length === 0) {
      return `${this.humanFieldName(fieldName)} is required`;
    }
    return null;
  }

  validateEmail(value, fieldName) {
    if (!value || value.trim().length === 0) {
      return "Email address is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) {
      return "Please enter a valid email address";
    }

    return null;
  }

  validatePhone(value, fieldName) {
    if (!value || value.trim().length === 0) {
      return "Mobile number is required";
    }

    const phone = value.trim();

    // South African phone validation
    // Allow formats: +27XXXXXXXXX, 0XXXXXXXXX, 27XXXXXXXXX
    const saPhoneRegex = /^(\+27|27|0)[6-8][0-9]{8}$/;

    if (!saPhoneRegex.test(phone.replace(/\s+/g, ''))) {
      return "Please enter a valid South African mobile number";
    }

    return null;
  }

  // Display field-level validation errors
  displayFieldError(field, error) {
    const container = field.closest('.relative');
    const errorElement = container.querySelector('.field-error');

    if (errorElement) {
      if (error) {
        errorElement.textContent = error;
        errorElement.classList.remove('hidden');
        field.classList.add('border-red-500');
        field.classList.remove('border-green-500');
      } else {
        errorElement.classList.add('hidden');
        errorElement.textContent = '';
        field.classList.remove('border-red-500');
        field.classList.add('border-green-500');
      }
    }
  }

  // Check if entire form is valid
  checkFormValidity() {
    const fields = this.customerValue ? ['firstName', 'lastName', 'phone'] : ['firstName', 'lastName', 'email', 'phone'];
    let allValid = true;

    fields.forEach(fieldName => {
      const targetName = `${fieldName}Target`;
      if (this[targetName]) {
        const field = this[targetName];
        const validator = this.validations[fieldName.replace('Target', '')];
        if (validator) {
          const error = validator.call(this, field.value, fieldName);
          if (error) allValid = false;
        }
      }
    });

    this.formValid = allValid;
    if (!this.formValid && this.submitTimeout) {
      clearTimeout(this.submitTimeout);
      this.submitTimeout = null;
    }
  }

  collectContactDetails() {
    const firstName = this.hasFirstNameTarget ? this.firstNameTarget.value.trim() : null;
    const lastName = this.hasLastNameTarget ? this.lastNameTarget.value.trim() : null;
    const phone = this.hasPhoneTarget ? this.phoneTarget.value.trim() : null;
    const email = (!this.customerValue && this.hasEmailTarget) ? this.emailTarget.value.trim() : null;

    const data = {
      first_name: firstName || null,
      last_name: lastName || null,
      phone: phone || null
    };

    if (email !== null) {
      data.email = email || null;
    }

    return data;
  }

  hasContactChanges(newData) {
    if (!this.lastSubmittedData) {
      return true;
    }

    const keys = ['first_name', 'last_name', 'phone'];
    if (!this.customerValue) {
      keys.push('email');
    }

    return keys.some(key => {
      const previous = this.lastSubmittedData[key] ?? null;
      const current = newData[key] ?? null;
      return (previous || null) !== (current || null);
    });
  }

  applyServerContact(contact) {
    const normalized = contact || {};

    if (normalized.first_name && this.hasFirstNameTarget) {
      this.firstNameTarget.value = normalized.first_name;
    }

    if (normalized.last_name && this.hasLastNameTarget) {
      this.lastNameTarget.value = normalized.last_name;
    }

    if (normalized.phone && this.hasPhoneTarget) {
      this.phoneTarget.value = normalized.phone;
    }

    if (normalized.email && this.hasEmailTarget) {
      this.emailTarget.value = normalized.email;
    }
  }

  // API call to update contact details
  async updateContactDetails(data) {
    const url = '/checkout/update_contact_details';

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to update contact details' };
      }

      return {
        success: true,
        message: result.message,
        contact: result.contact
      };
    } catch (error) {
      console.error('Contact details update error:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }

  // Get CSRF token for AJAX requests
  getCsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]');
    return token ? token.content : '';
  }

  // Form-level error display
  displayFormError(message, options = {}) {
    if (options.toast) {
      this.appendToast('error', 'Contact details update failed', message);
    }
  }

  // Show success message
  showSuccess(message) {
    this.appendToast('success', 'Contact details saved', message);
  }

  // Human-readable field names
  humanFieldName(fieldName) {
    const names = {
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email address',
      phone: 'Mobile number'
    };
    return names[fieldName] || fieldName;
  }

  appendToast(type, title, message, duration = 5000) {
    const container = document.getElementById("toast-container");
    if (!container) return;

    const config = this.toastConfig(type);
    const toastId = this.generateToastId();

    const toast = document.createElement("div");
    toast.id = toastId;
    toast.dataset.controller = "toast";
    toast.dataset.toastDurationValue = Number.isFinite(duration) ? duration.toString() : "5000";
    toast.dataset.action = "mouseenter->toast#pause mouseleave->toast#resume";
    toast.className = "pointer-events-auto w-80 max-w-full bg-white border border-gray-200 shadow-xl rounded-2xl transform transition-all duration-300 ease-out translate-y-6 opacity-0";

    const inner = document.createElement("div");
    inner.className = "p-4 flex items-start gap-3";

    const iconWrapper = document.createElement("div");
    iconWrapper.className = `flex items-center justify-center w-10 h-10 rounded-full ${config.accent}`;

    const icon = document.createElement("i");
    icon.className = config.icon;
    iconWrapper.appendChild(icon);

    const content = document.createElement("div");
    content.className = "flex-1 min-w-0";

    const titleElement = document.createElement("p");
    titleElement.className = `text-xs font-semibold uppercase tracking-wide ${config.titleClass}`;
    titleElement.textContent = title || config.defaultTitle;
    content.appendChild(titleElement);

    if (message) {
      const messageElement = document.createElement("p");
      messageElement.className = "text-sm text-gray-700 break-words mt-1";
      messageElement.textContent = message;
      content.appendChild(messageElement);
    }

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "text-gray-400 hover:text-gray-600 transition";
    closeButton.dataset.action = "click->toast#dismiss";
    closeButton.setAttribute("aria-label", "Close notification");

    const closeIcon = document.createElement("i");
    closeIcon.className = "fa-solid fa-xmark";
    closeButton.appendChild(closeIcon);

    inner.appendChild(iconWrapper);
    inner.appendChild(content);
    inner.appendChild(closeButton);

    toast.appendChild(inner);

    container.appendChild(toast);
  }

  toastConfig(type) {
    const defaults = {
      icon: "fa-solid fa-circle-info",
      accent: "bg-blue-100 text-blue-600",
      titleClass: "text-blue-600",
      defaultTitle: "Notice"
    };

    switch (type) {
      case "success":
        return {
          icon: "fa-solid fa-circle-check",
          accent: "bg-emerald-100 text-emerald-600",
          titleClass: "text-emerald-600",
          defaultTitle: "Success"
        };
      case "error":
        return {
          icon: "fa-solid fa-triangle-exclamation",
          accent: "bg-red-100 text-red-600",
          titleClass: "text-red-600",
          defaultTitle: "Something went wrong"
        };
      case "warning":
        return {
          icon: "fa-solid fa-triangle-exclamation",
          accent: "bg-amber-100 text-amber-600",
          titleClass: "text-amber-600",
          defaultTitle: "Heads up"
        };
      default:
        return defaults;
    }
  }

  generateToastId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return `toast-${window.crypto.randomUUID()}`;
    }
    return `toast-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6)}`;
  }
}
